import { useCallback, useRef, useEffect } from 'react'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { StepType } from 'src/graphql/types/steps'

import { ApplicantFiltersActionType } from '../../context/applicant-filters.reducer'
import {
  useFilters,
  useFiltersDispatch,
} from '../../context/ApplicantFiltersContext'
import { defaultFilters } from '../FiltersSiderbar/Filters'

import ApplicantFilterMenu from './ApplicantFilterMenu'
import { useApplicantFilters } from './hooks/useApplicantFilters'
import { ApplicantFilter } from './types'
import {
  ANY_ACTIVE_TASK,
  ANY_ASSIGNEE,
  applyAnyExclusivity,
} from './utils/taskFilterOptions'

interface ApplicantFilterMenuWrapperProps {
  onFilterChange?: () => void
}

const ApplicantFilterMenuWrapper = ({
  onFilterChange,
}: ApplicantFilterMenuWrapperProps) => {
  const filters = useFilters()
  const rawDispatchFilters = useFiltersDispatch()
  const filterConfigsRef = useRef<ApplicantFilter[]>([])

  // Wrap dispatch for user-initiated filter changes to notify parent (e.g., for page reset)
  const dispatchFilters = useCallback(
    (action) => {
      rawDispatchFilters(action)
      onFilterChange?.()
    },
    [rawDispatchFilters, onFilterChange]
  )

  const handleRecruitersChange = useCallback(
    (recruiters: string[]) => {
      // Find the current options from the filter configs
      const recruitersFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'recruiters'
      )
      const currentOptions =
        recruitersFilter?.options || filters.recruiters?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          recruiters: {
            ...filters.recruiters,
            selectedValues: recruiters,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleJobListingsChange = useCallback(
    (jobListings: string[]) => {
      // Find the current options from the filter configs
      const jobListingsFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'jobListings'
      )
      const currentOptions =
        jobListingsFilter?.options || filters.jobListings?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          jobListings: {
            ...filters.jobListings,
            selectedValues: jobListings,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleLeadSourcesChange = useCallback(
    (leadSources: string[]) => {
      // Find the current options from the filter configs
      const leadSourcesFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'leadSources'
      )
      const currentOptions =
        leadSourcesFilter?.options || filters.leadSources?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          leadSources: {
            ...filters.leadSources,
            selectedValues: leadSources,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleTagsChange = useCallback(
    (tags: string[]) => {
      // Find the current options from the filter configs
      const tagsFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'tags'
      )
      const currentOptions = tagsFilter?.options || filters.tags?.options || []

      // Load data if not already loaded to ensure options are available
      if (tagsFilter?.loadData && !tagsFilter.isLoaded) {
        tagsFilter.loadData()
      }

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          tags: {
            ...filters.tags,
            selectedValues: tags,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const setTaskMultiSelect = useCallback(
    (
      key: 'taskDeadline' | 'taskTemplates' | 'taskAssignees',
      selectedValues: string[]
    ) => {
      const config = filterConfigsRef.current.find((f) => f.urlKey === key)
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          // Full structure, so saved views that predate these filters still work
          [key]: {
            ...defaultFilters[key],
            ...filters[key],
            selectedValues,
            options: config?.options || filters[key]?.options || [],
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleTaskDeadlineChange = useCallback(
    (values: string[]) =>
      setTaskMultiSelect(
        'taskDeadline',
        applyAnyExclusivity(
          values,
          filters.taskDeadline?.selectedValues || [],
          ANY_ACTIVE_TASK
        )
      ),
    [filters, setTaskMultiSelect]
  )

  const handleTaskDueDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: { key: 'taskDueDate', from: value.gte, to: value.lte },
      })
    },
    [dispatchFilters]
  )

  const handleTaskTemplatesChange = useCallback(
    (templateIds: string[]) => setTaskMultiSelect('taskTemplates', templateIds),
    [setTaskMultiSelect]
  )

  const handleTaskAssigneesChange = useCallback(
    (employeeIds: string[]) =>
      setTaskMultiSelect(
        'taskAssignees',
        applyAnyExclusivity(
          employeeIds,
          filters.taskAssignees?.selectedValues || [],
          ANY_ASSIGNEE
        )
      ),
    [filters, setTaskMultiSelect]
  )

  const handleEndorsementsChange = useCallback(
    (endorsements: string[]) => {
      // Find the current options from the filter configs
      const endorsementsFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'endorsements'
      )
      const currentOptions =
        endorsementsFilter?.options || filters.endorsements?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          endorsements: {
            ...filters.endorsements,
            selectedValues: endorsements,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleStatesChange = useCallback(
    (states: string[]) => {
      // Find the current options from the filter configs
      const statesFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'states'
      )
      const currentOptions =
        statesFilter?.options || filters.states?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          states: {
            ...filters.states,
            selectedValues: states,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleEquipmentTypesChange = useCallback(
    (equipmentTypes: string[]) => {
      // Find the current options from the filter configs
      const equipmentTypesFilter = filterConfigsRef.current.find(
        (f) => f.urlKey === 'equipmentTypes'
      )
      const currentOptions =
        equipmentTypesFilter?.options || filters.equipmentTypes?.options || []

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          equipmentTypes: {
            ...filters.equipmentTypes,
            selectedValues: equipmentTypes,
            options: currentOptions,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleEligibleForHireChange = useCallback(
    (values: string[]) => {
      // Yes/No is single-select semantically (it maps to a scalar boolean), so
      // keep only the most recently chosen value — never both Yes and No.
      const selectedValues =
        values.length > 0 ? [values[values.length - 1]] : []

      // Update the boolean filter itself. Always write the complete structure
      // (key/type/title/options) so it works even when the filter is absent
      // from a pre-existing saved view — downstream code (GraphQL builder, URL
      // sync, pills) keys off `type === 'multi-select'`.
      const payload = {
        ...filters,
        eligibleForHire: {
          ...defaultFilters.eligibleForHire,
          ...filters.eligibleForHire,
          selectedValues,
          options:
            filters.eligibleForHire?.options ||
            defaultFilters.eligibleForHire?.options ||
            [],
        },
      }

      // Auto-select coupling (approved: "auto-select, adjustable"): when a
      // Yes/No value is chosen, also check Applicant Stage: TERMINATED so the
      // user can see and adjust/remove it. On clearing, leave the stage as-is.
      if (selectedValues.length > 0) {
        const currentStages = filters.applicantStages?.selectedValues || []
        const alreadyHasStage = currentStages.some(
          (stage) => stage.applicantStage === ApplicantStage.TERMINATED
        )
        if (!alreadyHasStage) {
          payload.applicantStages = {
            ...defaultFilters.applicantStages,
            ...filters.applicantStages,
            selectedValues: [
              ...currentStages,
              { applicantStage: ApplicantStage.TERMINATED },
            ],
            options:
              filters.applicantStages?.options ||
              defaultFilters.applicantStages?.options ||
              [],
            key: 'applicantStages',
            type: 'applicant-stage-filter',
            title: 'Applicant Stage',
          }
        }
      }

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload,
      })
    },
    [filters, dispatchFilters]
  )

  const handlePermanentlyDisqualifiedChange = useCallback(
    (values: string[]) => {
      // Yes/No is single-select semantically (it maps to a scalar boolean), so
      // keep only the most recently chosen value — never both Yes and No.
      const selectedValues =
        values.length > 0 ? [values[values.length - 1]] : []

      // Update the boolean filter itself. Always write the complete structure
      // (key/type/title/options) so it works even when the filter is absent
      // from a pre-existing saved view — downstream code (GraphQL builder, URL
      // sync, pills) keys off `type === 'multi-select'`.
      const payload = {
        ...filters,
        permanentlyDisqualified: {
          ...defaultFilters.permanentlyDisqualified,
          ...filters.permanentlyDisqualified,
          selectedValues,
          options:
            filters.permanentlyDisqualified?.options ||
            defaultFilters.permanentlyDisqualified?.options ||
            [],
        },
      }

      // Auto-select coupling (approved: "auto-select, adjustable"): when a
      // Yes/No value is chosen, also check Applicant Stage: DISQUALIFIED so the
      // user can see and adjust/remove it. On clearing, leave the stage as-is.
      if (selectedValues.length > 0) {
        const currentStages = filters.applicantStages?.selectedValues || []
        const alreadyHasStage = currentStages.some(
          (stage) => stage.applicantStage === ApplicantStage.DISQUALIFIED
        )
        if (!alreadyHasStage) {
          payload.applicantStages = {
            ...defaultFilters.applicantStages,
            ...filters.applicantStages,
            selectedValues: [
              ...currentStages,
              { applicantStage: ApplicantStage.DISQUALIFIED },
            ],
            options:
              filters.applicantStages?.options ||
              defaultFilters.applicantStages?.options ||
              [],
            key: 'applicantStages',
            type: 'applicant-stage-filter',
            title: 'Applicant Stage',
          }
        }
      }

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload,
      })
    },
    [filters, dispatchFilters]
  )

  const handleApplicantStagesChange = useCallback(
    (applicantStages: string[]) => {
      // Convert string array to ApplicantStageFilter array
      // Handle both simple stages and nested stages (e.g., "TERMINATED:ATTENDANCE_ISSUES")
      const applicantStageFilters = applicantStages.map((stage) => {
        if (stage.includes(':')) {
          const [applicantStage, reason] = stage.split(':')
          // For HIRED, don't add a reason field - just return the applicant stage
          if (applicantStage === 'HIRED') {
            return {
              applicantStage,
            }
          }
          return {
            applicantStage,
            [getReasonField(applicantStage)]: reason,
          }
        }
        return {
          applicantStage: stage,
        }
      })

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          applicantStages: {
            ...filters.applicantStages,
            selectedValues: applicantStageFilters,
            key: 'applicantStages',
            type: 'applicant-stage-filter',
            title: 'Applicant Stage',
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const getReasonField = (applicantStage: string) => {
    switch (applicantStage) {
      case 'PROSPECT':
        return 'hiringStage'
      case 'DISQUALIFIED':
        return 'disqualifiedReason'
      case 'NOT_INTERESTED':
        return 'notInterestedReason'
      case 'TERMINATED':
        return 'terminatedReason'
      default:
        return 'reason'
    }
  }

  const handleStepsChange = useCallback(
    (steps: string[]) => {
      // Convert string array to StepFilter array
      // Only include steps that have both type and status (e.g., "DOT_APP:IN_PROGRESS")
      // Filter out parent step types that don't have a status
      const builtInStepTypes = new Set<string>(Object.values(StepType))
      const stepFilters = steps
        .filter((step) => step.includes(':'))
        .map((step) => {
          const [value, status] = step.split(':')
          if (builtInStepTypes.has(value)) {
            return { type: value, status }
          }
          return { type: 'CUSTOM', customStepId: value, status }
        })

      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          steps: {
            ...filters.steps,
            selectedValues: stepFilters,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleYearsOfExperienceChange = useCallback(
    (value: { gte?: number | null; lte?: number | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          yearsOfExperience: {
            ...filters.yearsOfExperience,
            gte: value.gte,
            lte: value.lte,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleCallCountChange = useCallback(
    (value: { gte?: number | null; lte?: number | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          callCount: {
            ...filters.callCount,
            gte: value.gte,
            lte: value.lte,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleMissedCallCountChange = useCallback(
    (value: { gte?: number | null; lte?: number | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          missedOutboundCallCount: {
            ...filters.missedOutboundCallCount,
            gte: value.gte,
            lte: value.lte,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleZipcodeChange = useCallback(
    (zipcodes: string[]) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: {
          ...filters,
          zipCodes: {
            ...filters.zipCodes,
            values: zipcodes,
          },
        },
      })
    },
    [filters, dispatchFilters]
  )

  const handleApplicationDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'applicationDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleHiredDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'hiredDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleTerminatedDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'terminatedDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleDisqualifiedDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'disqualifiedDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleNotInterestedDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'notInterestedDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleAvailableForWorkDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'availableForWorkDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleBookingDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'bookingDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleLastContactDateChange = useCallback(
    (value: { gte?: Date | null; lte?: Date | null }) => {
      dispatchFilters({
        type: ApplicantFiltersActionType.DATE_FILTER_CHANGED,
        payload: {
          key: 'lastContactDate',
          from: value.gte,
          to: value.lte,
        },
      })
    },
    [dispatchFilters]
  )

  const handleCustomFieldChange = useCallback(
    (customFieldId: string, value: any) => {
      const currentValues = filters.customFields || []
      const existingIndex = currentValues.findIndex(
        (f) => f.customFieldId === customFieldId
      )
      const newValues = [...currentValues]

      // Check if value is empty
      const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        // Check for empty date objects where both gte and lte are null/undefined
        (value &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          (value.gte !== undefined || value.lte !== undefined) &&
          (value.gte === null || value.gte === undefined) &&
          (value.lte === null || value.lte === undefined))

      if (isEmpty) {
        // Remove filter
        if (existingIndex >= 0) {
          newValues.splice(existingIndex, 1)
        }
      } else {
        // Add or update filter
        const filterValue = buildCustomFieldFilterValue(value)

        if (existingIndex >= 0) {
          newValues[existingIndex] = { customFieldId, ...filterValue }
        } else {
          newValues.push({ customFieldId, ...filterValue })
        }
      }

      dispatchFilters({
        type: ApplicantFiltersActionType.CUSTOM_FIELDS_CHANGED,
        payload: newValues,
      })
    },
    [filters, dispatchFilters]
  )

  const buildCustomFieldFilterValue = (value: any) => {
    if (typeof value === 'string') {
      return { stringValue: value }
    }
    if (typeof value === 'boolean') {
      return { booleanValue: value }
    }
    if (value && typeof value === 'object') {
      if (value.gte !== undefined || value.lte !== undefined) {
        return { dateValue: value }
      }
      if (Array.isArray(value)) {
        return { selectValues: value }
      }
    }
    return {}
  }

  const handleClearAll = useCallback(() => {
    dispatchFilters({
      type: ApplicantFiltersActionType.CLEAR_ALL,
    })
  }, [dispatchFilters])

  const selectedApplicantStages =
    filters.applicantStages?.selectedValues?.map((s) => {
      if (s.hiringStage) return `${s.applicantStage}:${s.hiringStage}`
      if (s.disqualifiedReason)
        return `${s.applicantStage}:${s.disqualifiedReason}`
      if (s.notInterestedReason)
        return `${s.applicantStage}:${s.notInterestedReason}`
      if (s.terminatedReason) return `${s.applicantStage}:${s.terminatedReason}`
      return s.applicantStage
    }) || []

  const { filters: filterConfigs } = useApplicantFilters({
    selectedRecruiters: filters.recruiters?.selectedValues || [],
    selectedJobListings: filters.jobListings?.selectedValues || [],
    selectedLeadSources: filters.leadSources?.selectedValues || [],
    selectedTags: filters.tags?.selectedValues || [],
    selectedEndorsements: filters.endorsements?.selectedValues || [],
    selectedStates: filters.states?.selectedValues || [],
    selectedEquipmentTypes: filters.equipmentTypes?.selectedValues || [],
    selectedTaskDeadline: filters.taskDeadline?.selectedValues || [],
    selectedTaskDueDate: {
      gte: filters.taskDueDate?.gte,
      lte: filters.taskDueDate?.lte,
    },
    selectedTaskTemplates: filters.taskTemplates?.selectedValues || [],
    selectedTaskAssignees: filters.taskAssignees?.selectedValues || [],
    selectedEligibleForHire: filters.eligibleForHire?.selectedValues || [],
    selectedPermanentlyDisqualified:
      filters.permanentlyDisqualified?.selectedValues || [],
    selectedApplicantStages,
    selectedSteps:
      filters.steps?.selectedValues?.map((s) => {
        const value = s.customStepId ?? s.type
        if (s.status) return `${value}:${s.status}`
        return value
      }) || [],
    selectedYearsOfExperience: {
      gte: filters.yearsOfExperience?.gte,
      lte: filters.yearsOfExperience?.lte,
    },
    selectedCallCount: {
      gte: filters.callCount?.gte,
      lte: filters.callCount?.lte,
    },
    selectedMissedCallCount: {
      gte: filters.missedOutboundCallCount?.gte,
      lte: filters.missedOutboundCallCount?.lte,
    },
    selectedZipcodes: Array.isArray(filters.zipCodes?.values)
      ? filters.zipCodes.values
      : [],
    selectedApplicationDate: {
      gte: filters.applicationDate?.gte,
      lte: filters.applicationDate?.lte,
    },
    selectedAvailableForWorkDate: {
      gte: filters.availableForWorkDate?.gte,
      lte: filters.availableForWorkDate?.lte,
    },
    selectedHiredDate: {
      gte: filters.hiredDate?.gte,
      lte: filters.hiredDate?.lte,
    },
    selectedTerminatedDate: {
      gte: filters.terminatedDate?.gte,
      lte: filters.terminatedDate?.lte,
    },
    selectedDisqualifiedDate: {
      gte: filters.disqualifiedDate?.gte,
      lte: filters.disqualifiedDate?.lte,
    },
    selectedNotInterestedDate: {
      gte: filters.notInterestedDate?.gte,
      lte: filters.notInterestedDate?.lte,
    },
    selectedBookingDate: {
      gte: filters.bookingDate?.gte,
      lte: filters.bookingDate?.lte,
    },
    selectedLastContactDate: {
      gte: filters.lastContactDate?.gte,
      lte: filters.lastContactDate?.lte,
    },
    selectedCustomFieldValues: filters.customFields || [],
    onRecruitersChange: handleRecruitersChange,
    onJobListingsChange: handleJobListingsChange,
    onLeadSourcesChange: handleLeadSourcesChange,
    onTagsChange: handleTagsChange,
    onEndorsementsChange: handleEndorsementsChange,
    onStatesChange: handleStatesChange,
    onEquipmentTypesChange: handleEquipmentTypesChange,
    onTaskDeadlineChange: handleTaskDeadlineChange,
    onTaskDueDateChange: handleTaskDueDateChange,
    onTaskTemplatesChange: handleTaskTemplatesChange,
    onTaskAssigneesChange: handleTaskAssigneesChange,
    onEligibleForHireChange: handleEligibleForHireChange,
    onPermanentlyDisqualifiedChange: handlePermanentlyDisqualifiedChange,
    onApplicantStagesChange: handleApplicantStagesChange,
    onStepsChange: handleStepsChange,
    onYearsOfExperienceChange: handleYearsOfExperienceChange,
    onCallCountChange: handleCallCountChange,
    onMissedCallCountChange: handleMissedCallCountChange,
    onZipcodeChange: handleZipcodeChange,
    onApplicationDateChange: handleApplicationDateChange,
    onAvailableForWorkDateChange: handleAvailableForWorkDateChange,
    onHiredDateChange: handleHiredDateChange,
    onTerminatedDateChange: handleTerminatedDateChange,
    onDisqualifiedDateChange: handleDisqualifiedDateChange,
    onNotInterestedDateChange: handleNotInterestedDateChange,
    onBookingDateChange: handleBookingDateChange,
    onLastContactDateChange: handleLastContactDateChange,
    onCustomFieldChange: handleCustomFieldChange,
  })

  // Keep the ref updated with the latest filterConfigs
  useEffect(() => {
    filterConfigsRef.current = filterConfigs
  }, [filterConfigs])

  // When options that label a selected value load, copy them into the global
  // filter state so the chips show names instead of ids
  useEffect(() => {
    const needsLabels = (config: ApplicantFilter) => {
      const currentFilter = filters[config.urlKey]
      if (currentFilter?.type !== 'multi-select' || !config.options?.length) {
        return false
      }
      const hasLabel = (options, value) =>
        options?.some((option) => option?.value === value)
      return (currentFilter.selectedValues || []).some(
        (value) =>
          !hasLabel(currentFilter.options, value) &&
          hasLabel(config.options, value)
      )
    }

    const toUpdate = filterConfigs.filter(
      (config) => config.urlKey && needsLabels(config)
    )

    if (toUpdate.length) {
      const updatedFilters = { ...filters }
      toUpdate.forEach((config) => {
        updatedFilters[config.urlKey] = {
          ...filters[config.urlKey],
          options: config.options,
        }
      })

      // Use raw dispatch — this is a system update (loading options), not a user action
      rawDispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: updatedFilters,
      })
    }
  }, [filterConfigs, filters, rawDispatchFilters])

  return (
    <ApplicantFilterMenu filters={filterConfigs} onClearAll={handleClearAll} />
  )
}

export default ApplicantFilterMenuWrapper
